import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { CalendarDays, Clock, KeyRound, MapPin, Mail, Package, Smartphone, User, Bell, BellOff, Save } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import ExpiredScreen from "@/components/customer/ExpiredScreen";
import { useAuth } from "@/context/AuthContext";
import { getExistingSubscription, getPushPermissionState, isPushSupported, subscribePush, unsubscribePush } from "@/lib/push";

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated. Use it next time you log in.");
      onOpenChange(false);
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Choose a strong password of at least 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cur">Current password</Label>
            <PasswordInput id="cur" required value={cur} onChange={(e) => setCur(e.target.value)} testid="cp-current" />
          </div>
          <div>
            <Label htmlFor="next">New password</Label>
            <PasswordInput id="next" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} testid="cp-new" />
          </div>
          <div>
            <Label htmlFor="conf">Confirm new password</Label>
            <PasswordInput id="conf" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="cp-confirm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="cp-submit">{busy ? "Updating..." : "Update password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CoverageBlock({ title, cov }) {
  if (!cov) return null;
  const t = cov.type;
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="h-4 w-4 text-primary" />
        <div className="font-medium">{title}</div>
        <Badge variant="secondary" className="capitalize">{t.replace("_", " ")}</Badge>
      </div>
      {t === "pan_india" && (
        <div className="text-sm text-muted-foreground">Full Pan India coverage — every state, every city.</div>
      )}
      {t === "state" && (
        <div className="text-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">States</div>
          <div className="flex flex-wrap gap-1.5">{(cov.states || []).map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
        </div>
      )}
      {t === "city" && (
        <div className="text-sm space-y-2">
          {(cov.states || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">States</div>
              <div className="flex flex-wrap gap-1.5">{cov.states.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Cities</div>
            <div className="flex flex-wrap gap-1.5">{(cov.cities || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
          </div>
        </div>
      )}
      {t === "district" && (
        <div className="text-sm space-y-2">
          {(cov.states || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">States</div>
              <div className="flex flex-wrap gap-1.5">{cov.states.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Districts</div>
            <div className="flex flex-wrap gap-1.5">{(cov.districts || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
          </div>
        </div>
      )}
      {t === "roc" && (
        <div className="text-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">ROCs</div>
          <div className="flex flex-wrap gap-1.5">{(cov.rocs || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
        </div>
      )}
    </div>
  );
}

export default function MyPackage() {
  const { loading, active, subscription, days_left } = useCustomer();
  const { user } = useAuth();
  const [showCP, setShowCP] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(false);

  // Daily notification email
  const [notifEmail, setNotifEmail] = useState("");
  const [notifSaved, setNotifSaved] = useState("");
  const [notifBusy, setNotifBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customer/notification-email");
        setNotifEmail(data.notification_email || "");
        setNotifSaved(data.notification_email || "");
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const saveNotifEmail = async () => {
    const val = notifEmail.trim();
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error("Please enter a valid email address"); return;
    }
    setNotifBusy(true);
    try {
      await api.put("/customer/notification-email", { email: val || null });
      setNotifSaved(val);
      toast.success(val ? "Notifications will now be sent to this email" : "Cleared — notifications go to your login email");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setNotifBusy(false); }
  };

  // Push notification state
  const [pushSupported] = useState(isPushSupported());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPerm, setPushPerm] = useState(getPushPermissionState());
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!pushSupported) return;
      const sub = await getExistingSubscription();
      setPushSubscribed(!!sub);
      setPushPerm(getPushPermissionState());
    })();
  }, [pushSupported]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
        setPushSubscribed(false);
        toast.success("Notifications turned off");
      } else {
        await subscribePush();
        setPushSubscribed(true);
        toast.success("You'll be notified the moment new data lands");
      }
      setPushPerm(getPushPermissionState());
    } catch (e) {
      toast.error(e.message || "Could not update notification setting");
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    if (outcome === "accepted") toast.success("App installed! Look for the icon on your home screen.");
    setInstallEvt(null);
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!active) return <ExpiredScreen />;

  const sub = subscription;

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex items-center gap-2">
        <div className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center">
          <User className="h-4 w-4" />
        </div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl">My Package</h1>
      </div>

      {/* Profile */}
      <Card className="p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Name</div>
            <div className="mt-1 font-medium">{user?.name}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Company</div>
            <div className="mt-1 font-medium">{user?.company}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
            <div className="mt-1 num text-sm">{user?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Phone</div>
            <div className="mt-1 num text-sm">{user?.phone || "—"}</div>
          </div>
        </div>
      </Card>

      {/* Subscription hero */}
      {sub && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center">
                <Package className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Package</div>
                <div className="mt-1 font-heading font-bold text-2xl">{sub.data_type.toUpperCase()}</div>
                <div className="text-xs text-muted-foreground mt-1 num">{sub.duration_days || 30} day plan</div>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center">
                <CalendarDays className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Subscription window</div>
                <div className="mt-1 text-sm num">
                  <div>{format(new Date(sub.approved_at), "dd MMM yyyy")} →</div>
                  <div>{format(new Date(sub.expires_at), "dd MMM yyyy")}</div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center">
                <Clock className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Days remaining</div>
                <div className="mt-1 font-heading font-bold text-3xl num text-primary" data-testid="days-remaining">{days_left ?? 0}</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Coverage details */}
      {sub && (
        <Card className="p-5 mb-6">
          <div className="font-heading font-semibold text-lg mb-3">Coverage details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sub.gst_coverage && <CoverageBlock title="GST" cov={sub.gst_coverage} />}
            {sub.mca_coverage && <CoverageBlock title="MCA" cov={sub.mca_coverage} />}
          </div>
        </Card>
      )}

      {/* Daily Email Notification */}
      <Card className="p-5 mb-6" data-testid="notification-email-card">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-4 w-4 text-primary" />
          <div className="font-heading font-semibold text-lg">Daily Email Notification</div>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Where should we send new-data emails? Leave blank to use your login email <span className="num">({user?.email})</span>. Emails are strictly limited to the packages you subscribe to.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="email"
            value={notifEmail}
            onChange={(e) => setNotifEmail(e.target.value)}
            placeholder="notifications@yourcompany.com"
            className="flex-1 min-w-[220px] h-10 rounded-md border border-border bg-background px-3 text-sm"
            data-testid="notification-email-input"
          />
          <Button onClick={saveNotifEmail} disabled={notifBusy || notifEmail.trim() === notifSaved} data-testid="notification-email-save">
            <Save className="h-4 w-4 mr-1.5" /> {notifBusy ? "Saving..." : "Save"}
          </Button>
        </div>
        {notifSaved && (
          <div className="mt-2 text-xs text-muted-foreground">Currently sending to: <span className="num text-foreground">{notifSaved}</span></div>
        )}
      </Card>

      {/* Account actions */}
      <Card className="p-5">
        <div className="font-heading font-semibold text-lg mb-3">Account</div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => setShowCP(true)} data-testid="open-change-password">
            <KeyRound className="h-4 w-4 mr-1.5" /> Change password
          </Button>
          {pushSupported && (
            <Button
              variant={pushSubscribed ? "outline" : "default"}
              onClick={togglePush}
              disabled={pushBusy || pushPerm === "denied"}
              data-testid="push-toggle-btn"
            >
              {pushSubscribed ? <BellOff className="h-4 w-4 mr-1.5" /> : <Bell className="h-4 w-4 mr-1.5" />}
              {pushBusy ? "..." : (pushSubscribed ? "Turn off notifications" : "Notify me on new data")}
            </Button>
          )}
          {installEvt && !installed && (
            <Button onClick={install} data-testid="install-app-btn">
              <Smartphone className="h-4 w-4 mr-1.5" /> Install app on this device
            </Button>
          )}
          {installed && (
            <Badge className="self-center bg-emerald-500/10 text-emerald-600 border-emerald-500/30" variant="outline">App installed</Badge>
          )}
        </div>
        {pushPerm === "denied" && (
          <div className="mt-3 text-xs text-amber-600 dark:text-amber-400">
            Notifications are blocked at the browser level. Open your browser site settings and re-enable notifications for this site.
          </div>
        )}
        {!pushSupported && (
          <div className="mt-3 text-xs text-muted-foreground">
            Notifications need a modern browser (Chrome, Edge, Firefox, or Safari 16+). Check back on a supported browser to enable push alerts.
          </div>
        )}
        {!installEvt && !installed && (
          <div className="mt-3 text-xs text-muted-foreground">
            Tip: To install the app on your phone, open this site in Chrome or Edge and tap <span className="font-medium">Menu → Install app</span> (or “Add to Home Screen” on iPhone Safari).
          </div>
        )}
      </Card>

      <ChangePasswordDialog open={showCP} onOpenChange={setShowCP} />
    </div>
  );
}
